/** Cloud Portal MQTT types and motion constants for the Smartwinch UI. */

export const WINCH_STATES = {
    idle: 'idle',
    moving: 'moving',
    descending: 'descending',
    holding: 'holding',
    service: 'service',
    calibrating: 'calibrating',
} as const;

export type WinchMotionState = (typeof WINCH_STATES)[keyof typeof WINCH_STATES];

export type WinchLimits = {
    distance: { min: number; max: number };
    depth: { min: number; max: number };
};

export type NamedWinchPosition = {
    id: string;
    name?: string;
    position: { distance: number; depth: number };
};

export type WinchTelemetry = {
    state: WinchMotionState;
    distance: number | null;
    depth: number | null;
    targetDistance: number | null;
    targetDepth: number | null;
    calibrated: boolean;
    allowSmartMovement: boolean;
    timestamp: string | null;
};

/** Named presets from getposition. Live coords stay on telemetry. */
export type WinchStatusSnapshot = {
    limits: WinchLimits | null;
    speed: number | null;
    savedPositions: NamedWinchPosition[];
    servicePosition: { distance: number; depth: number } | null;
    measurementPosition: { distance: number; depth: number } | null;
};

export type WinchMqttUser = {
    id: number;
    name: string;
    email: string;
    role: string;
};

export type MqttCommandResult = {
    success: boolean;
    description?: string;
    uuid?: string;
};

export type VerticalScanUpdateSource = 'status' | 'enable' | 'disable' | 'setlimits';

/** Current vertical-scan config from status / enable / disable / setlimits responses. */
export type VerticalScanConfig = {
    enabled: boolean;
    x: number | null;
    yMin: number;
    yMax: number;
    percentageFromMiddle?: number;
    totalSweepTime?: number;
    stepMoveTime?: number;
    startDelay?: number;
};

export type WinchMqttTopics = {
    publish: {
        getPosition: string;
        setPosition: string;
        goto: string;
        stop: string;
        status: string;
        verticalScanEnable: string;
        verticalScanDisable: string;
        verticalScanSetLimits: string;
        verticalScanStatus: string;
    };
    subscribe: {
        online: string;
        telemetry: string;
        getPosition: string;
        setPosition: string;
        goto: string;
        stop: string;
        status: string;
        verticalScanEnable: string;
        verticalScanDisable: string;
        verticalScanSetLimits: string;
        verticalScanStatus: string;
    };
};

export const EMPTY_WINCH_USER: WinchMqttUser = {
    id: 0,
    name: 'webportal',
    email: '',
    role: '',
};

const BUSY_WINCH_STATES: ReadonlySet<string> = new Set([
    WINCH_STATES.moving,
    WINCH_STATES.descending,
    WINCH_STATES.holding,
    WINCH_STATES.calibrating,
]);

export function isWinchMoving(state: string | null | undefined): boolean {
    return state != null && BUSY_WINCH_STATES.has(state);
}
